/**
 * Deal List View — table alternative to the kanban board with sortable columns.
 */

import { useState, useMemo, useCallback } from 'react'
import { useCrmStore } from '@/stores/crmStore'
import { TrendingUp } from 'lucide-react'
import { formatCents } from '@/utils/currency'
import { SortableHeader } from '@/components/ui/SortableHeader'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  EmptyState,
  Spinner,
  Badge,
} from '@/components/ui'
import type { Pipeline } from '@/types/crm'

interface DealListViewProps {
  pipeline: Pipeline
}

export function DealListView({ pipeline }: DealListViewProps) {
  const deals = useCrmStore((s) => s.deals)
  const isLoading = useCrmStore((s) => s.isLoadingDeals)
  const setSelectedDeal = useCrmStore((s) => s.setSelectedDeal)

  const [sortBy, setSortBy] = useState('updated')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const handleSort = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy)
    setSortOrder(newSortOrder)
  }, [])

  // Client-side sort since all deals are loaded for kanban
  const sortedDeals = useMemo(() => {
    const sorted = [...deals]
    sorted.sort((a, b) => {
      let cmp = 0
      switch (sortBy) {
        case 'name': cmp = (a.name || '').localeCompare(b.name || ''); break
        case 'amount': cmp = (a.amountCents || 0) - (b.amountCents || 0); break
        case 'close_date': cmp = (a.expectedCloseDate || '').localeCompare(b.expectedCloseDate || ''); break
        case 'created': cmp = (a.createdAt || '').localeCompare(b.createdAt || ''); break
        case 'updated':
        default: cmp = (a.updatedAt || '').localeCompare(b.updatedAt || ''); break
      }
      return sortOrder === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [deals, sortBy, sortOrder])

  const getStage = (stageId: string) => pipeline.stages.find((s) => s.id === stageId)

  if (isLoading && deals.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    )
  }

  if (deals.length === 0) {
    return (
      <EmptyState icon={TrendingUp} title="No deals yet" description="Create your first deal to get started" />
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHeader label="Deal" sortKey="name" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={handleSort} className="px-6" />
          <TableHead>Contact</TableHead>
          <TableHead>Stage</TableHead>
          <SortableHeader label="Amount" sortKey="amount" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={handleSort} align="right" />
          <SortableHeader label="Expected Close" sortKey="close_date" currentSortBy={sortBy} currentSortOrder={sortOrder} onSort={handleSort} />
          <TableHead>Source</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedDeals.map((deal) => {
          const stage = getStage(deal.stageId)
          return (
            <TableRow
              key={deal.id}
              onClick={() => setSelectedDeal(deal)}
              className="hover:bg-muted cursor-pointer"
            >
              <TableCell className="px-6 py-3">
                <div className="font-medium text-foreground">{deal.name}</div>
                {deal.tags && deal.tags.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {deal.tags.slice(0, 2).map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {deal.contactName || '—'}
              </TableCell>
              <TableCell>
                {stage && (
                  /* basis: hardcoded-surface guard — text-white sits on the admin-configured stage.color hex; keep fixed in both modes */
                  <span
                    className="inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium text-white"
                    style={{ backgroundColor: stage.color }}
                  >
                    {stage.name}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right font-medium text-foreground">
                {formatCents(deal.amountCents, deal.currency)}
              </TableCell>
              <TableCell className="text-muted-foreground text-xs">
                {deal.expectedCloseDate || '—'}
              </TableCell>
              <TableCell className="text-muted-foreground text-xs">
                {deal.source || '—'}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
